import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function AdminSettings() {
  const [referralOnly, setReferralOnly] = useState(null);
  const [season, setSeason] = useState("");
  const [seasonBusy, setSeasonBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [brokers, setBrokers] = useState(null);
  const [newBroker, setNewBroker] = useState("");
  const [editBroker, setEditBroker] = useState(null); // {id, name}
  const [brokerBusy, setBrokerBusy] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((res) => {
        setReferralOnly(res.data.referral_only);
        setSeason(res.data.current_season || "2026");
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const loadBrokers = () => {
    api.get("/admin/broker-catalog").then((res) => setBrokers(res.data)).catch(() => setBrokers([]));
  };
  useEffect(() => {
    loadBrokers();
  }, []);

  const addBroker = async () => {
    setBrokerBusy(true);
    try {
      await api.post("/admin/broker-catalog", { name: newBroker });
      toast.success("Broker added — visible in new registrations");
      setNewBroker("");
      loadBrokers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBrokerBusy(false);
    }
  };

  const toggleBroker = async (b) => {
    setBrokerBusy(true);
    try {
      await api.put(`/admin/broker-catalog/${b.id}`, { name: b.name, status: b.status === "active" ? "inactive" : "active" });
      toast.success(b.status === "active" ? "Broker disabled — hidden from new registrations" : "Broker enabled");
      loadBrokers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBrokerBusy(false);
    }
  };

  const saveBrokerName = async () => {
    setBrokerBusy(true);
    try {
      await api.put(`/admin/broker-catalog/${editBroker.id}`, { name: editBroker.name, status: brokers.find((x) => x.id === editBroker.id)?.status || "active" });
      toast.success("Broker renamed — member records updated");
      setEditBroker(null);
      loadBrokers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBrokerBusy(false);
    }
  };

  const deleteBroker = async (b) => {
    if (!window.confirm(`Delete broker "${b.name}"? Only possible while no member is assigned.`)) return;
    setBrokerBusy(true);
    try {
      await api.delete(`/admin/broker-catalog/${b.id}`);
      toast.success("Broker deleted");
      loadBrokers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBrokerBusy(false);
    }
  };

  const saveSeason = async () => {
    const value = season.trim();
    if (!/^\d{4}$/.test(value)) {
      toast.error("Season must be a 4-digit year, e.g. 2026");
      return;
    }
    setSeasonBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        referral_only: referralOnly,
        current_season: value,
      });
      setSeason(data.current_season);
      toast.success(`Current season set to ${data.current_season} — highest-rank tracking starts fresh`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSeasonBusy(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { referral_only: !referralOnly });
      setReferralOnly(data.referral_only);
      toast.success(
        data.referral_only
          ? "Referral-only registration enabled"
          : "Referral-only registration disabled — open registration is now allowed",
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings-page">
      <div>
        <p className="eyebrow-label mb-2">Configuration</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Settings</h1>
      </div>

      <div className="card-lux p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-md">
            <h2 className="font-heading text-lg font-semibold text-white">Referral-Only Registration</h2>
            <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
              When enabled, new members must provide a valid IB referral code to register.
              When disabled, the referral code becomes optional and open registration is allowed.
            </p>
          </div>
          <button
            onClick={toggle}
            disabled={busy || referralOnly === null}
            data-testid="referral-only-toggle"
            role="switch"
            aria-checked={referralOnly === true}
            data-state={referralOnly ? "on" : "off"}
            className={`relative h-8 w-14 rounded-full border transition-colors duration-300 shrink-0 disabled:opacity-50 ${
              referralOnly ? "bg-[#D4AF37]/20 border-[#D4AF37]/60" : "bg-[#0B0C10] border-[#222530]"
            }`}
            aria-label="Toggle referral-only registration"
          >
            <span
              className={`absolute top-1 h-6 w-6 rounded-full transition-all duration-300 ${
                referralOnly ? "left-7 bg-[#D4AF37]" : "left-1 bg-[#64748B]"
              }`}
            />
          </button>
        </div>
        <p className="mt-4 text-xs font-mono tracking-widest uppercase" data-testid="referral-only-status">
          {referralOnly === null ? "Loading..." : referralOnly ? (
            <span className="text-emerald-400">Enabled — referral code required</span>
          ) : (
            <span className="text-red-400">Disabled — open registration</span>
          )}
        </p>
      </div>

      <div className="card-lux p-8" data-testid="admin-settings-season-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-md">
            <h2 className="font-heading text-lg font-semibold text-white">Current Season</h2>
            <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
              Seasons drive each member's Highest Rank Achievement. Starting a new season begins fresh
              tracking for every member, while previous seasons remain preserved for historical records.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <input
              className="input-lux w-28 font-mono text-center"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              maxLength={4}
              inputMode="numeric"
              placeholder="2026"
              data-testid="season-input"
            />
            <button
              onClick={saveSeason}
              disabled={seasonBusy || referralOnly === null}
              className="btn-gold px-5 py-2.5 text-xs disabled:opacity-60"
              data-testid="season-save-button"
            >
              {seasonBusy ? "Saving..." : "Save Season"}
            </button>
          </div>
        </div>
      </div>

      <div className="card-lux p-8" data-testid="broker-management-card">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-5">
          <div className="max-w-md">
            <h2 className="font-heading text-lg font-semibold text-white">Broker Management</h2>
            <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
              Active brokers appear in the registration dropdown. Inactive brokers are hidden from new
              registrations, but members already assigned keep their existing broker record. Brokers assigned
              to members can never be deleted.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <input
              className="input-lux w-56 font-mono uppercase"
              value={newBroker}
              onChange={(e) => setNewBroker(e.target.value.toUpperCase())}
              placeholder="NEW BROKER NAME"
              data-testid="broker-add-input"
            />
            <button onClick={addBroker} disabled={brokerBusy || newBroker.trim().length < 2} className="btn-gold px-5 py-2.5 text-xs disabled:opacity-60" data-testid="broker-add-button">
              Add Broker
            </button>
          </div>
        </div>
        {brokers === null ? (
          <p className="text-sm text-[#64748B]">Loading brokers…</p>
        ) : (
          <div className="divide-y divide-[#222530]" data-testid="broker-list">
            {brokers.map((b) => (
              <div key={b.id} className="flex items-center gap-4 py-3.5 flex-wrap" data-testid={`broker-row-${b.name}`}>
                {editBroker?.id === b.id ? (
                  <input className="input-lux flex-1 min-w-[180px] font-mono uppercase !py-2" value={editBroker.name} onChange={(e) => setEditBroker({ ...editBroker, name: e.target.value.toUpperCase() })} data-testid="broker-edit-input" />
                ) : (
                  <p className="text-sm text-white font-medium flex-1 min-w-[180px]">{b.name}</p>
                )}
                <p className="text-[10px] text-[#64748B] font-mono shrink-0">{b.assigned_count} member(s)</p>
                <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border shrink-0 ${b.status === "active" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : "text-[#94A3B8] border-[#222530]"}`} data-testid={`broker-status-${b.name}`}>
                  {b.status}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {editBroker?.id === b.id ? (
                    <>
                      <button onClick={saveBrokerName} disabled={brokerBusy} className="btn-gold px-3 py-1.5 text-[11px] disabled:opacity-50" data-testid="broker-save-button">Save</button>
                      <button onClick={() => setEditBroker(null)} className="btn-gold-outline px-3 py-1.5 text-[11px]" data-testid="broker-cancel-button">Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setEditBroker({ id: b.id, name: b.name })} className="btn-gold-outline px-3 py-1.5 text-[11px]" data-testid={`broker-edit-${b.name}`}>Rename</button>
                  )}
                  <button onClick={() => toggleBroker(b)} disabled={brokerBusy} className={`text-[11px] rounded-lg px-2.5 py-1.5 border transition-colors ${b.status === "active" ? "text-amber-400 border-amber-400/30 hover:bg-amber-400/10" : "text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"}`} data-testid={`broker-toggle-${b.name}`}>
                    {b.status === "active" ? "Disable" : "Enable"}
                  </button>
                  {b.assigned_count === 0 && (
                    <button onClick={() => deleteBroker(b)} className="text-[11px] text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`broker-delete-${b.name}`}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
